import { AccredibleButtonLoadingComponentModule } from '@accredible-frontend-v2/components/button-loading';
import { AccredibleTextInputComponentModule } from '@accredible-frontend-v2/forms/text-input';
import { AccredibleBaseFormComponent } from '@accredible-frontend-v2/forms/utils';
import { mustMatchValidator, passwordValidator } from '@accredible-frontend-v2/forms/validators';
import { TopBorderCardComponent } from '@accredible-frontend-v2/new-components/top-border-card';
import { TwoColumnsLayoutComponent } from '@accredible-frontend-v2/new-components/two-columns-layout';
import { AccredibleCookiesService } from '@accredible-frontend-v2/services/cookies';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleToastService } from '@accredible-frontend-v2/toast';
import { Component, ElementRef, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { finalize } from 'rxjs/operators';
import { CtaPanelComponent } from '../cta-panel/cta-panel.component';
import { EmailSentRouterLinkAction } from '../email-sent/email-sent.component';
import { EmailSentComponentModule } from '../email-sent/email-sent.component.module';
import { ResetPasswordApiService } from './reset-password.service';

const RESET_TOKEN_COOKIE = 'reset_token';

@Component({
  templateUrl: './reset-password.container.html',
  styleUrls: [`./reset-password.container.scss`],
  standalone: true,
  imports: [
    CtaPanelComponent,
    TwoColumnsLayoutComponent,
    TopBorderCardComponent,
    TranslocoModule,
    ReactiveFormsModule,
    EmailSentComponentModule,
    AccredibleTextInputComponentModule,
    AccredibleButtonLoadingComponentModule,
  ],
})
export class ResetPasswordContainer
  extends AccredibleBaseFormComponent<{ newPassword: string; confirmPassword: string }>
  implements OnInit
{
  protected readonly _el = inject(ElementRef<HTMLElement>);

  private readonly _resetPasswordApi = inject(ResetPasswordApiService);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _toast = inject(AccredibleToastService);
  private readonly _title = inject(Title);
  private readonly _router = inject(Router);
  private readonly _cookies = inject(AccredibleCookiesService);

  isPasswordSaved = signal(false);
  isLoading = signal(false);

  readonly routerLinkAction: EmailSentRouterLinkAction = {
    text: this._language.selectTranslate('reset-password.password_saved.sign_in'),
    routerLink: '/login',
  };

  constructor() {
    super(inject(ElementRef<HTMLElement>));

    this._title.setTitle('Accredible • Accounts • Reset Password');
  }

  ngOnInit(): void {
    this.formGroup = this._fb.group(
      {
        newPassword: [
          null,
          { validators: [Validators.required, passwordValidator()], updateOn: 'blur' },
        ],
        confirmPassword: [null, Validators.required],
      },
      {
        validators: mustMatchValidator('newPassword', 'confirmPassword'),
      },
    );
  }

  submit(): void {
    if (!this._isFormValid()) {
      return;
    }

    this._toast.dismissCurrentToast();
    this.isLoading.set(true);
    const resetToken = this._cookies.get(RESET_TOKEN_COOKIE);
    const newPassword = this.formGroup.get('newPassword').value;

    this._resetPasswordApi
      .resetPassword(newPassword, resetToken)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.isPasswordSaved.set(true);
          this._cookies.delete(RESET_TOKEN_COOKIE);
        },
        error: (res) => {
          if (res.status === 401) {
            this._toast.error(this._language.translate('reset-password.reset_token_expired'));
            this._cookies.delete(RESET_TOKEN_COOKIE);
            this._router.navigate(['/forgot-password']).then();
          } else {
            this._toast.error(this._language.translate('reset-password.form.error'));
          }
        },
      });
  }
}
